<template>
    <div
        id="lt-yantr-v2"
        class="student-payment-calculator">
        <div class="student-loan-wrapper blue-wrap-student">
            <div class="container">
                <p class="student-payment-block-description">
                    Calculate your estimated monthly payment and see how much interest you’ll pay over the life of your loan.
                </p>
            </div>
        </div>
        <div class="student-calculation-outer">
            <div class="student-filter-inputs">
                <form
                    ref="form"
                    @submit.prevent="studentLoanCalculation()">
                    <ul class="filter-block">
                        <li>
                            <number-input
                                v-model="StudentLoanBalance"
                                :label="labelStudentLoanBalance"
                                :class="error.studentLoanBalanceError ? 'error-border-red' : ''"
                                :maxlength="6"
                                :minvalue="1"
                                input-name="studentLoanBalance"
                                inputmode="decimal"
                                :show-symbol="false">
                                <template #leftimage>
                                    <div class="input-group-addon">
                                        <img src="../../assets/images/dollar-icon.svg" />
                                    </div>
                                </template>
                                <template #errormessage>
                                    <div class="error-text">
                                        <p v-if="error.studentLoanBalanceError">
                                            {{ error.studentLoanBalanceError }}
                                        </p>
                                    </div>
                                </template>
                            </number-input>
                        </li>
                        <li>
                            <number-input
                                v-model="AverageInterestRate"
                                :label="labelAverageInterestRate"
                                :class="error.averageInterestError ? 'error-border-red' : ''"
                                :precision="3"
                                max-value="99.999"
                                input-name="averageInterest"
                                inputmode="decimal"
                                :show-symbol="false"
                                no-decimal="true"
                                @input="validateAverageInterestRate()">
                                <template #rightimage>
                                    <div class="input-group-addon">
                                        <img src="../../assets/images/percentage-icon.svg" />
                                    </div>
                                </template>
                                <template #errormessage>
                                    <div class="error-text">
                                        <p v-if="error.averageInterestError">
                                            {{ error.averageInterestError }}
                                        </p>
                                    </div>
                                </template>
                            </number-input>
                        </li>
                        <li>
                            <number-input
                                v-model="LoanTerm"
                                :label="labelLoanTerm"
                                :class="error.loanTermError ? 'error-border-red' : ''"
                                :maxlength="2"
                                input-name="loanTerm"
                                :minvalue="1"
                                inputmode="decimal"
                                symbol="year(s)"
                                symbol-aligned="right">
                                <template #errormessage>
                                    <div class="error-text">
                                        <p v-if="error.loanTermError">
                                            {{ error.loanTermError }}
                                        </p>
                                    </div>
                                </template>
                            </number-input>
                        </li>
                        <li class="calculate-btn">
                            <button
                                type="submit"
                                class="btn btn-blue">
                                {{ calculateButtonText }}
                            </button>
                        </li>
                    </ul>
                </form>
            </div>
        </div>
        <div class="student-result-wrapper">
            <calculation-result
                v-if="MonthlyPayment !== '' && TotalInterest !== ''"
                :monthly-payment="MonthlyPayment"
                :total-interest="TotalInterest"
                :prop-student-loan-balance="PropsStudentLoanBalance"
                :open-disclosure-modal="openDisclosureModal"
                :disclosure-tokens="disclosureTokens"
            />
            <default-result v-else />
        </div>
    </div>
</template>
<script>
    import Accounting from 'accounting';
    import NumberInput from '~/components/number-input.vue';
    import CalculationResult from './components/calculation-result.vue';
    import DefaultResult from './components/default-result.vue';

    export default {
        components: {
            NumberInput,
            CalculationResult,
            DefaultResult,
        },
        props: {
            averageInterestRate: {
                default: 5,
            },
            balanceAmount: {
                default: 30000,
            },
            calculateButtonText: {
                default: 'Calculate',
            },
            disclosureText: {
                default: 'Disclosures',
            },
            disclosureTokens: {
                default: '00001',
            },

            labelAverageInterestRate: {
                default: 'Average Interest Rate',
            },
            labelLoanTerm: {
                default: 'Loan Term',
            },
            labelStudentLoanBalance: {
                default: 'Student Loan Balance',
            },
            loanTerm: {
                default: 10,
            },
            openDisclosureModal: {
                default: true,
            },
        },
        data() {
            return {
                DisclosureText: this.disclosureText,
                DisclosureTokens: this.disclosureTokens,
                StudentLoanBalance: Accounting.format(this.balanceAmount),
                AverageInterestRate: Accounting.formatMoney(this.averageInterestRate, { symbol: '', precision: 3 }),
                LoanTerm: this.loanTerm,
                MonthlyPayment: '',
                TotalInterest: '',
                PropsStudentLoanBalance: this.balanceAmount,
                error: {
                    studentLoanBalanceError: '',
                    averageInterestError: '',
                    loanTermError: '',
                },
            };
        },
        mounted() {
            this.validateAverageInterestRate();
        },
        methods: {
            validateAverageInterestRate() {
                this.AverageInterestRate = this.AverageInterestRate > 99.999 ? 99.999 : this.AverageInterestRate;
            },
            calculatePayment(amount, apr, years) {
                const principal = parseFloat(amount);
                const interest = parseFloat(apr) / 100 / 12;
                const payments = parseFloat(years) * 12;

                const avgMonthlyPayment = (1 + interest) ** payments;
                const monthlyPayment = principal * avgMonthlyPayment * interest / (avgMonthlyPayment - 1);
                return monthlyPayment;
            },
            studentLoanCalculation() {
                const studentLoanBalance = Accounting.unformat(this.$refs.form.studentLoanBalance.value);
                this.PropsStudentLoanBalance = Accounting.unformat(this.StudentLoanBalance);
                if (studentLoanBalance < 1) {
                    this.error.studentLoanBalanceError = 'Student Loan Balance should be 1 to 999999';
                } else {
                    this.error.studentLoanBalanceError = '';
                }
                if (this.$refs.form.loanTerm.value < 1) {
                    this.error.loanTermError = 'Loan Term should be 1 to 99';
                } else {
                    this.error.loanTermError = '';
                }

                if (this.error.studentLoanBalanceError === '' && this.error.loanTermError === '') {
                    const calculationResult = this.calculatePayment(Accounting.unformat(this.StudentLoanBalance), this.AverageInterestRate, this.LoanTerm);
                    this.MonthlyPayment = Math.round(calculationResult);
                    const totalInterest = calculationResult * (this.LoanTerm * 12) - Accounting.unformat(this.StudentLoanBalance);
                    this.TotalInterest = Math.round(totalInterest);
                }
                this.AverageInterestRate = Accounting.formatMoney(this.AverageInterestRate, { symbol: '', precision: 3 });
                this.StudentLoanBalance = Accounting.format(this.StudentLoanBalance);
            },
        },
    };
</script>
<style src="../../assets/styles/v2/common.scss" lang="scss"></style>
<style src="./main.scss" lang="scss"></style>
<style src="../../assets/styles/a11yModal.scss" lang="scss"></style>
